/**
 * Bridge between the axios layer (plain modules, no React) and AuthProvider.
 *
 * The 401 interceptor in performRequest.js lives outside the React tree, so it
 * cannot call setToken/useNavigate directly. Rather than have the api layer
 * import the auth context (a coupling that would also make every service module
 * pull in React), it broadcasts a window CustomEvent that AuthProvider listens
 * for. One direction, no import cycle, and the event is inspectable in devtools.
 *
 * The latch lives here — not in the interceptor and not in the provider —
 * because it is the one place every expiry signal passes through. Ten parallel
 * requests failing 401 at once therefore produce exactly one dispatch, and so
 * one toast and one redirect.
 */

export const AUTH_EXPIRED_EVENT = "auth:expired";

let expiryNotified = false;

/**
 * Announce that the session is over. Returns true if this call was the one that
 * actually dispatched, false if an expiry was already in flight.
 * @param {string} reason - "401" | "cookie-missing", for logging/telemetry only.
 */
export const notifyAuthExpired = (reason = "unknown") => {
  if (expiryNotified) return false;
  expiryNotified = true;
  window.dispatchEvent(
    new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { reason } })
  );
  return true;
};

/**
 * Close the latch WITHOUT dispatching, for a deliberate logout (the logout
 * button, or the websocket logout message).
 *
 * A deliberate logout clears the cookies itself, so any request already in
 * flight comes back 401 and would otherwise be reported as an expiry — toasting
 * "your session has ended" over a logout the user just asked for. Marking the
 * transition as already handled makes those late 401s no-ops.
 *
 * Note this is why merely moving resetAuthExpiry() out of logout() is not
 * enough: at logout time the latch is normally already open (false), so the
 * in-flight 401 would fire regardless of whether logout re-armed it. The
 * suppression has to be positive, not just the absence of a reset.
 */
export const suppressAuthExpiry = () => {
  expiryNotified = true;
};

/**
 * Re-arm the latch. Called on login ONLY — a fresh session is the single event
 * that should make expiry reportable again. Without this, a user who signs back
 * in after an expiry would never be redirected again.
 */
export const resetAuthExpiry = () => {
  expiryNotified = false;
};

/** Test/diagnostic helper: is an expiry already being handled? */
export const isAuthExpiryPending = () => expiryNotified;
